/**
 * Admin Dashboard Page
 * Comprehensive admin interface for user management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  VerifiedUser as VerifiedIcon,
  Block as BlockIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { API_BASE_URL } from '../config/api';

interface User {
  id: string;
  email: string;
  name: string;
  company?: string;
  role: 'USER' | 'ADMIN' | 'MODERATOR';
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  subscription?: {
    tier: 'FREE' | 'PROFESSIONAL' | 'ENTERPRISE';
    status: 'ACTIVE' | 'TRIALING' | 'PAST_DUE' | 'CANCELLED' | 'UNPAID';
    trialEnd?: string;
    currentPeriodEnd: string;
  };
  usageTracking?: Array<{
    recipesCreated: number;
    waterProfilesCreated: number;
    formulationsCreated: number;
    customSaltsCreated: number;
  }>;
  _count?: {
    formulations: number;
    waterProfiles: number;
    customSalts: number;
  };
}

interface AdminStats {
  users: {
    total: number;
    active: number;
    verified: number;
    unverified: number;
    monthlySignups: number;
    lastMonthSignups: number;
    growth: number;
  };
  subscriptions: Array<{
    tier: string;
    status: string;
    _count: number;
  }>;
  emails: Array<{
    status: string;
    _count: number;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminDashboard: React.FC = () => {
  const { user, token } = useSelector((state: RootState) => state.auth);
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVerified, setFilterVerified] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Check if user is admin
  if (!user || user.role !== 'ADMIN') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You don't have permission to access the admin dashboard.</Typography>
        </Alert>
      </Container>
    );
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterRole) params.append('role', filterRole);
      if (filterTier) params.append('tier', filterTier);
      if (filterStatus) params.append('status', filterStatus);
      if (filterVerified) params.append('verified', filterVerified);

      const response = await fetch(`${API_BASE_URL}/admin/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.data.users);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({ open: true, message: 'Failed to fetch users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleVerifyEmail = async (userId: string) => {
    try {
      console.log('Attempting to verify email for user:', userId);
      console.log('API URL:', `${API_BASE_URL}/admin/users/${userId}/verify-email`);
      console.log('Token:', token ? 'Present' : 'Missing');

      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/verify-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (response.ok) {
        setSnackbar({ open: true, message: 'Email verified successfully', severity: 'success' });
        fetchUsers();
      } else {
        throw new Error(responseData.error || 'Failed to verify email');
      }
    } catch (error) {
      console.error('Error verifying email:', error);
      setSnackbar({ open: true, message: `Failed to verify email: ${error instanceof Error ? error.message : 'Unknown error'}`, severity: 'error' });
    }
  };

  const handleSuspendUser = async (userId: string, suspend: boolean) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ suspended: suspend, reason: suspend ? 'Admin action' : 'Reactivated by admin' })
      });

      if (response.ok) {
        setSnackbar({ 
          open: true, 
          message: `User ${suspend ? 'suspended' : 'activated'} successfully`, 
          severity: 'success' 
        });
        fetchUsers();
      } else {
        throw new Error(`Failed to ${suspend ? 'suspend' : 'activate'} user`);
      }
    } catch (error) {
      console.error(`Error ${suspend ? 'suspending' : 'activating'} user:`, error);
      setSnackbar({ 
        open: true, 
        message: `Failed to ${suspend ? 'suspend' : 'activate'} user`, 
        severity: 'error' 
      });
    }
  };

  const handleResendVerification = async (userId: string) => {
    try {
      console.log('Attempting to resend verification for user:', userId);
      console.log('API URL:', `${API_BASE_URL}/admin/users/${userId}/resend-verification`);

      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Resend response status:', response.status);
      console.log('Resend response ok:', response.ok);

      const responseData = await response.json();
      console.log('Resend response data:', responseData);

      if (response.ok) {
        setSnackbar({ open: true, message: 'Verification email sent', severity: 'success' });
      } else {
        throw new Error(responseData.error || 'Failed to resend verification');
      }
    } catch (error) {
      console.error('Error resending verification:', error);
      setSnackbar({ open: true, message: `Failed to resend verification email: ${error instanceof Error ? error.message : 'Unknown error'}`, severity: 'error' });
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchUsers();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterRole, filterTier, filterStatus, filterVerified]);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'ENTERPRISE': return 'secondary';
      case 'PROFESSIONAL': return 'primary';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'TRIALING': return 'info';
      case 'PAST_DUE': return 'warning';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" />
        Admin Dashboard
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={stats?.users.total || 0} color="primary">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon />
                  Users
                </Box>
              </Badge>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssessmentIcon />
                Analytics
              </Box>
            } 
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* User Management Tab */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  label="Role"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="USER">User</MenuItem>
                  <MenuItem value="ADMIN">Admin</MenuItem>
                  <MenuItem value="MODERATOR">Moderator</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Tier</InputLabel>
                <Select
                  value={filterTier}
                  onChange={(e) => setFilterTier(e.target.value)}
                  label="Tier"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="FREE">Free</MenuItem>
                  <MenuItem value="PROFESSIONAL">Professional</MenuItem>
                  <MenuItem value="ENTERPRISE">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="TRIALING">Trialing</MenuItem>
                  <MenuItem value="PAST_DUE">Past Due</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Verified</InputLabel>
                <Select
                  value={filterVerified}
                  onChange={(e) => setFilterVerified(e.target.value)}
                  label="Verified"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Verified</MenuItem>
                  <MenuItem value="false">Unverified</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email Status</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{user.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                        {user.company && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {user.company}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.emailVerified ? <VerifiedIcon /> : <EmailIcon />}
                        label={user.emailVerified ? 'Verified' : 'Unverified'}
                        color={user.emailVerified ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.subscription ? (
                        <Box>
                          <Chip
                            label={user.subscription.tier}
                            color={getTierColor(user.subscription.tier)}
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                          <br />
                          <Chip
                            label={user.subscription.status}
                            color={getStatusColor(user.subscription.status)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      ) : (
                        <Chip label="No Subscription" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'ADMIN' ? 'secondary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Suspended'}
                        color={user.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedUser(user);
                              setUserDetailOpen(true);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {!user.emailVerified && (
                          <Tooltip title="Verify Email">
                            <IconButton
                              size="small"
                              onClick={() => handleVerifyEmail(user.id)}
                              color="primary"
                            >
                              <VerifiedIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!user.emailVerified && (
                          <Tooltip title="Resend Verification">
                            <IconButton
                              size="small"
                              onClick={() => handleResendVerification(user.id)}
                              color="info"
                            >
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={user.isActive ? 'Suspend User' : 'Activate User'}>
                          <IconButton
                            size="small"
                            onClick={() => handleSuspendUser(user.id, user.isActive)}
                            color={user.isActive ? 'error' : 'success'}
                          >
                            <BlockIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Analytics Tab */}
        {stats && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {stats.users.total}
                  </Typography>
                  <Typography variant="body2" color={stats.users.growth >= 0 ? 'success.main' : 'error.main'}>
                    {stats.users.growth >= 0 ? '+' : ''}{stats.users.growth.toFixed(1)}% from last month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Users
                  </Typography>
                  <Typography variant="h4">
                    {stats.users.active}
                  </Typography>
                  <Typography variant="body2">
                    {((stats.users.active / stats.users.total) * 100).toFixed(1)}% of total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Verified Users
                  </Typography>
                  <Typography variant="h4">
                    {stats.users.verified}
                  </Typography>
                  <Typography variant="body2">
                    {((stats.users.verified / stats.users.total) * 100).toFixed(1)}% verified
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Monthly Signups
                  </Typography>
                  <Typography variant="h4">
                    {stats.users.monthlySignups}
                  </Typography>
                  <Typography variant="body2">
                    This month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* User Detail Dialog */}
      <Dialog open={userDetailOpen} onClose={() => setUserDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                <Typography><strong>Name:</strong> {selectedUser.name}</Typography>
                <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
                <Typography><strong>Company:</strong> {selectedUser.company || 'Not specified'}</Typography>
                <Typography><strong>Role:</strong> {selectedUser.role}</Typography>
                <Typography><strong>Status:</strong> {selectedUser.isActive ? 'Active' : 'Suspended'}</Typography>
                <Typography><strong>Email Verified:</strong> {selectedUser.emailVerified ? 'Yes' : 'No'}</Typography>
                <Typography><strong>Created:</strong> {new Date(selectedUser.createdAt).toLocaleString()}</Typography>
                {selectedUser.lastLogin && (
                  <Typography><strong>Last Login:</strong> {new Date(selectedUser.lastLogin).toLocaleString()}</Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Subscription</Typography>
                {selectedUser.subscription ? (
                  <>
                    <Typography><strong>Tier:</strong> {selectedUser.subscription.tier}</Typography>
                    <Typography><strong>Status:</strong> {selectedUser.subscription.status}</Typography>
                    <Typography><strong>Period End:</strong> {new Date(selectedUser.subscription.currentPeriodEnd).toLocaleDateString()}</Typography>
                    {selectedUser.subscription.trialEnd && (
                      <Typography><strong>Trial End:</strong> {new Date(selectedUser.subscription.trialEnd).toLocaleDateString()}</Typography>
                    )}
                  </>
                ) : (
                  <Typography>No subscription</Typography>
                )}
                
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Usage</Typography>
                {selectedUser._count && (
                  <>
                    <Typography><strong>Formulations:</strong> {selectedUser._count.formulations}</Typography>
                    <Typography><strong>Water Profiles:</strong> {selectedUser._count.waterProfiles}</Typography>
                    <Typography><strong>Custom Salts:</strong> {selectedUser._count.customSalts}</Typography>
                  </>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;
